import { useQuery } from '@tanstack/react-query';
import apiClient from '../utils/apiClient';
import { TimePeriod } from '../types/api';

// Query keys for individual Satisfaction KPIs
export const SATISFACTION_KPI_KEYS = {
  AVERAGE: 'satisfactionAverage',
  TOTAL_SURVEYS: 'satisfactionTotalSurveys',
  BEST_SHIFT: 'satisfactionBestShift',
  BEST_ORDER_TYPE: 'satisfactionBestOrderType',
} as const;

// API service functions for individual KPIs
const apiService = {
  fetchAverageSatisfaction: async (branchId: number, timeRange?: TimePeriod) => {
    const response = await apiClient.get(`/satisfaction/${branchId}/kpis/average`, {
      params: timeRange ? { timeRange } : {}
    });
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch average satisfaction data');
    }
  },

  fetchTotalSurveys: async (branchId: number, timeRange?: TimePeriod) => {
    const response = await apiClient.get(`/satisfaction/${branchId}/kpis/total-surveys`, {
      params: timeRange ? { timeRange } : {}
    });
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch total surveys data');
    }
  },

  fetchBestShift: async (branchId: number, timeRange?: TimePeriod) => {
    const response = await apiClient.get(`/satisfaction/${branchId}/kpis/best-shift`, {
      params: timeRange ? { timeRange } : {}
    });
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch best shift data');
    }
  },

  fetchBestOrderType: async (branchId: number, timeRange?: TimePeriod) => {
    const response = await apiClient.get(`/satisfaction/${branchId}/kpis/best-order-type`, {
      params: timeRange ? { timeRange } : {}
    });
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch best order type data');
    }
  },
};

// Hook for average satisfaction KPI
export function useSatisfactionAverage(branchId?: number, timeRange?: TimePeriod) {
  return useQuery({
    queryKey: [SATISFACTION_KPI_KEYS.AVERAGE, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchAverageSatisfaction(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Hook for total surveys KPI
export function useSatisfactionTotalSurveys(branchId?: number, timeRange?: TimePeriod) {
  return useQuery({
    queryKey: [SATISFACTION_KPI_KEYS.TOTAL_SURVEYS, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchTotalSurveys(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for best shift KPI
export function useSatisfactionBestShift(branchId?: number, timeRange?: TimePeriod) {
  return useQuery({
    queryKey: [SATISFACTION_KPI_KEYS.BEST_SHIFT, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchBestShift(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for best order type KPI
export function useSatisfactionBestOrderType(branchId?: number, timeRange?: TimePeriod) {
  return useQuery({
    queryKey: [SATISFACTION_KPI_KEYS.BEST_ORDER_TYPE, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchBestOrderType(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}